import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, StatusBadge } from "@/components/Bits";
import { toast } from "sonner";
import { UserPlus, Key, Copy, CheckCircle, Prohibit, ArrowCounterClockwise, Trash, ShieldCheck } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function TeamUsersPanel() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [credModal, setCredModal] = useState(null); // {email, password, title}
  const [busyId, setBusyId] = useState(null);
  const [policy, setPolicy] = useState(null); // {account_required, is_account_owner}
  const [policyBusy, setPolicyBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/users");
      setUsers(data.users);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/auth/2fa/status").then(({ data }) => setPolicy(data)).catch(() => {}); }, []);

  const toggleRequire2fa = async (next) => {
    setPolicyBusy(true);
    try {
      await api.put("/auth/2fa/account-policy", { require_2fa: next });
      setPolicy((p) => ({ ...p, account_required: next }));
      toast.success(next ? "2FA is now required for your whole practice" : "2FA requirement removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPolicyBusy(false); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset two-factor for ${u.email}? They'll be asked to set up a new authenticator on their next sign-in.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/auth/users/${u.id}/reset-2fa`);
      toast.success(`Two-factor reset for ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const setActive = async (u, active) => {
    setBusyId(u.id);
    try {
      await api.patch(`/auth/users/${u.id}`, { active });
      toast.success(`${u.email} ${active ? "reactivated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const changeRole = async (u, role) => {
    setBusyId(u.id);
    try {
      await api.patch(`/auth/users/${u.id}`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const resetPassword = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/auth/users/${u.id}/reset-password`);
      setCredModal({ email: data.email, password: data.temp_password, title: "Password reset" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This permanently deletes their login. This cannot be undone.`)) return;
    setBusyId(u.id);
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`${u.email} removed`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusyId(null); }
  };

  return (
    <>
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="overline text-orangeink mb-1">Administration</div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-navy">Team &amp; Users</h2>
          <p className="text-sm text-navy/70 mt-1 max-w-2xl">Create accounts, set roles, reset passwords and deactivate access. New accounts are created here — self sign-up is disabled.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-btn"
          className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orange transition-colors duration-200">
          <UserPlus size={18} /> Add user
        </button>
      </div>

      {policy?.is_account_owner && (
        <div className="flex items-center justify-between gap-4 flex-wrap bg-white border border-slate-200 rounded-xl p-4 mb-5" data-testid="require-2fa-panel">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} weight="fill" className={policy.account_required ? "text-positive mt-0.5" : "text-navy/65 mt-0.5"} />
            <div>
              <div className="text-navy font-semibold text-sm">Require two-factor for everyone</div>
              <p className="text-navy/70 text-xs max-w-xl mt-0.5">
                When on, every member of your practice must set up an authenticator app before they can access the workspace.
              </p>
            </div>
          </div>
          <button onClick={() => toggleRequire2fa(!policy.account_required)} disabled={policyBusy}
            data-testid="require-2fa-toggle" role="switch" aria-checked={policy.account_required}
            className={`relative w-12 h-7 rounded-full transition-colors shrink-0 disabled:opacity-60 ${policy.account_required ? "bg-positive" : "bg-slate-300"}`}>
            <span className={`absolute top-1 left-0 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${policy.account_required ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="border-b border-slate-200 overline text-navy/65">
              <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>Created</Th><Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.email}`}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <Td className="font-medium text-navy">{u.name || "—"}{isSelf && <span className="ml-2 text-xs text-orangeink">(you)</span>}</Td>
                  <Td mono className="text-navy/70">{u.email}</Td>
                  <Td>
                    <Select value={u.role} onValueChange={(v) => v !== u.role && changeRole(u, v)} disabled={isSelf || busyId === u.id}>
                      <SelectTrigger data-testid={`role-select-${u.email}`} className="h-8 w-[110px] rounded-lg border-slate-200 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="user">user</SelectItem>
                        <SelectItem value="viewer">viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </Td>
                  <Td><div className="flex items-center gap-2">
                    <StatusBadge value={u.active ? "Active" : "Inactive"} testid={`user-status-${u.email}`} />
                    {u.two_factor_enabled && (
                      <span title="Two-factor enabled" data-testid={`user-2fa-${u.email}`}
                        className="inline-flex items-center gap-1 text-[0.7rem] font-semibold text-positive bg-positive/10 px-1.5 py-0.5 rounded-full">
                        <ShieldCheck size={11} weight="fill" /> 2FA
                      </span>
                    )}
                  </div></Td>
                  <Td mono className="text-navy/70 text-xs">{u.created_at ? fmtDate(u.created_at) : "—"}</Td>
                  <Td right>
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => resetPassword(u)} disabled={busyId === u.id} data-testid={`reset-pw-${u.email}`}
                        title="Reset password"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors whitespace-nowrap disabled:opacity-50">
                        <ArrowCounterClockwise size={14} /> Reset password
                      </button>
                      {u.two_factor_enabled && (
                        <button onClick={() => reset2fa(u)} disabled={busyId === u.id} data-testid={`reset-2fa-${u.email}`}
                          title="Reset two-factor (user re-enrols on next login)"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors whitespace-nowrap disabled:opacity-50">
                          <ShieldCheck size={14} /> Reset 2FA
                        </button>
                      )}
                      {u.active ? (
                        <button onClick={() => setActive(u, false)} disabled={isSelf || busyId === u.id} data-testid={`deactivate-${u.email}`}
                          title={isSelf ? "You cannot deactivate yourself" : "Deactivate"}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-negative/40 text-negative hover:bg-negative hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <Prohibit size={14} /> Deactivate
                        </button>
                      ) : (
                        <button onClick={() => setActive(u, true)} disabled={busyId === u.id} data-testid={`activate-${u.email}`}
                          title="Reactivate"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-positive/40 text-positive hover:bg-positive hover:text-white transition-colors disabled:opacity-50">
                          <CheckCircle size={14} /> Activate
                        </button>
                      )}
                      {!isSelf && (
                        <button onClick={() => removeUser(u)} disabled={busyId === u.id} data-testid={`delete-user-${u.email}`}
                          title="Remove user"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-negative/40 text-negative hover:bg-negative hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <Trash size={14} /> Remove
                        </button>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && users.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="users-empty">No users yet.</div>
        )}
        {loading && <div className="py-16 text-center font-mono text-navy/65 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>

      <AddUserDialog open={showAdd} onClose={() => setShowAdd(false)}
        onCreated={(cred) => { setShowAdd(false); setCredModal(cred); load(); }} />

      <CredentialDialog cred={credModal} onClose={() => setCredModal(null)} />
    </>
  );
}

export default function Users() {
  return <Page><TeamUsersPanel /></Page>;
}

function AddUserDialog({ open, onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("user");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setEmail(""); setName(""); setRole("user"); } }, [open]);

  const submit = async () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/users", { email: email.trim(), name: name.trim(), role });
      onCreated({ email: data.user.email, password: data.temp_password, title: "Account created" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-lg border-slate-200" data-testid="add-user-dialog">
        <DialogHeader>
          <div className="overline text-orangeink mb-1">New account</div>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight text-navy">Add a user</DialogTitle>
          <DialogDescription className="text-navy/70">A temporary password will be generated for you to share.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Full name">
            <input data-testid="new-user-name" value={name} onChange={(e) => setName(e.target.value)}
              className="acc-input2" placeholder="Jane Auditor" />
          </Field>
          <Field label="Email">
            <input data-testid="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="acc-input2" placeholder="jane@practice.co" />
          </Field>
          <Field label="Role">
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="new-user-role" className="rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">viewer — read-only, cannot change or export</SelectItem>
                <SelectItem value="user">user — full access except user management</SelectItem>
                <SelectItem value="admin">admin — full access incl. user management</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={onClose} data-testid="add-user-cancel"
              className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="add-user-submit"
              className="px-5 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
              {saving ? "CREATING…" : "Create user"}
            </button>
          </div>
        </div>
        <style>{`.acc-input2{width:100%;background:#fff;border:1px solid rgba(11,26,47,.2);border-radius:4px;padding:.6rem .8rem;font-size:.875rem;color:#0E0F3F;outline:none;transition:all .15s}.acc-input2:focus{border-color:#F37A24;box-shadow:0 0 0 2px rgba(46,163,253,.25)}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function CredentialDialog({ cred, onClose }) {
  const copy = () => {
    navigator.clipboard?.writeText(`Email: ${cred.email}\nTemporary password: ${cred.password}`);
    toast.success("Copied to clipboard");
  };
  return (
    <Dialog open={!!cred} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-lg border-slate-200" data-testid="credential-dialog">
        <DialogHeader>
          <div className="overline text-orangeink mb-1 flex items-center gap-2"><Key size={14} /> {cred?.title}</div>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight text-navy">Share these credentials</DialogTitle>
          <DialogDescription className="text-navy/70">
            This temporary password is shown only once. Copy it now and share it securely with the user — they should change it after first login.
          </DialogDescription>
        </DialogHeader>
        {cred && (
          <div className="space-y-3">
            <div className="bg-navy/[0.03] border border-slate-200 rounded-lg p-4 space-y-2 font-mono text-sm">
              <div><span className="text-navy/65 text-xs uppercase tracking-wider">Email</span><div className="text-navy" data-testid="cred-email">{cred.email}</div></div>
              <div><span className="text-navy/65 text-xs uppercase tracking-wider">Temporary password</span><div className="text-navy font-semibold" data-testid="cred-password">{cred.password}</div></div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={copy} data-testid="copy-cred-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
                <Copy size={15} /> Copy
              </button>
              <button onClick={onClose} data-testid="cred-done-btn"
                className="px-5 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors">Done</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-navy/70 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const Th = ({ children, right }) => <th className={`px-5 py-3 ${right ? "text-right" : "text-left"} font-semibold whitespace-nowrap`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) =>
  <td className={`px-5 py-3.5 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>;
